import { useLayoutEffect, useRef, useState } from 'react';
import { renderMarkdown } from '../plan/markdown';
import type { MarkdownLinks } from '../plan/markdown';

/**
 * Prose written where it is read: rendered until it is pressed, then its own
 * field in its own type and place — no editor around it and no mode to switch
 * into. Leaving it, or Esc, shows it rendered again. A link inside it is a way
 * somewhere, not a place to write.
 *
 * What is typed stays in the field while it has focus, so a write coming back
 * from the vault never moves the cursor.
 */
export function EditableProse({
  text,
  onChange,
  links,
  placeholder = 'Write here',
  label = 'Text',
}: {
  text: string;
  onChange: (next: string) => void;
  links: MarkdownLinks;
  placeholder?: string;
  label?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  if (draft !== null) {
    return (
      <textarea
        ref={field}
        className="doc-body doc-field"
        aria-label={label}
        value={draft}
        autoFocus
        spellCheck
        onChange={(e) => {
          setDraft(e.target.value);
          onChange(e.target.value);
        }}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') e.currentTarget.blur();
        }}
      />
    );
  }

  return (
    <div
      className="doc-body doc-editable"
      role="textbox"
      tabIndex={0}
      aria-label={`${label} — press to write`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a')) return;
        setDraft(text);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          setDraft(text);
        }
      }}
    >
      {text.trim() ? renderMarkdown(text, links) : <p className="doc-placeholder">{placeholder}</p>}
    </div>
  );
}
